import { useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { useAudioVideo, useMeetingManager, useToggleLocalMute } from 'amazon-chime-sdk-component-library-react'
import { XIcon } from 'lucide-react'

import { useMeetingStore } from '@stores/meeting'
import { useDepartedParticipantsStore } from '@/stores/departedParticipants'
import useParticipantNotifications from '@hooks/useParticipantNotifications'
import { useParticipantJoinNotification } from '@hooks/useParticipantJoinNotification'
import { useCallDuration } from '@hooks/useCallDuration'
import { useDeviceSelection } from '@hooks/useDeviceSelection'
import { useLeave } from '@hooks/useLeaveMeeting'
import { useNetworkConnectionAlert } from '@hooks/useNetworkConnectionAlert'
import { usePhoneWaitingSpeech } from '@/hooks/usePhoneWaitingSpeech'
import { MeetingDataProvider, useMeetingData } from '@/contexts/MeetingDataContext'
import { LoadingState } from '@components/atoms/LoadingState/LoadingState'
import { NetworkConnectionAlert } from '@/components/molecules/NetworkConnectionAlert'
import { AppTemplate } from '@/components/templates/AppTemplate/AppTemplate'
import { MeetingHeader } from '@/components/molecules/Header/MeetingHeader'
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/Avatar/Avatar'
import { DeviceSelector } from '@/components/molecules/DeviceSelector/DeviceSelector'
import { PeripheralToggleButton } from '@/components/atoms/PeripheralToggleButton'
import { MeetingActionButton } from '@/components/molecules/MeetingActionButton/MeetingActionButton'

export function PhoneMeetingPage() {
  const { meetingId: routeMeetingId } = useParams<{
    appointmentId?: string
    meetingId?: string
  }>()

  const meetingManager = useMeetingManager()
  const audioVideo = useAudioVideo()

  const storeMeetingId = useMeetingStore((s) => s.meetingId)

  const effectiveMeetingId = storeMeetingId ?? routeMeetingId?.trim() ?? null

  const isMeetingReady = !!meetingManager.meetingSession
  const isMeetingConnected = !!audioVideo

  if (!effectiveMeetingId) return null

  if (!isMeetingReady || !isMeetingConnected) {
      return <LoadingState message="Connexion à la réunion…" />
    }

  return (
    <MeetingDataProvider>
      <MeetingContent />
    </MeetingDataProvider>
  )
}

function MeetingContent() {
  useParticipantNotifications()
  usePhoneWaitingSpeech()

  const callDuration = useCallDuration(true)
  const clearDeparted = useDepartedParticipantsStore((s) => s.clearDeparted)
  const { appointment } = useMeetingData()

  const { leave, isLeaving } = useLeave('phone')
  const { muted, toggleMute } = useToggleLocalMute()
  const { audioInputs, audioOutputs, selectedAudioInput, selectedAudioOutput, selectAudioInput, selectAudioOutput } =
    useDeviceSelection({ autoSelect: false })
  useParticipantJoinNotification(selectedAudioOutput)

  const participant = appointment?.interlocutor
  const { showAlert, dismissAlert } = useNetworkConnectionAlert(participant ?? null)

  // Nettoyer le store des participants partis au démontage
  useEffect(() => {
    return () => {
      clearDeparted()
    }
  }, [clearDeparted])

  return (
    <>
      {participant && (
        <NetworkConnectionAlert
          isOpen={showAlert}
          onClose={dismissAlert}
          interlocutor={participant}
        />
      )}
      <AppTemplate
        header={
          <MeetingHeader centered interlocutorFirstName={participant?.firstName} phoneNumber={participant?.phone} />
        }
        className="bg-primary text-white"
      >
        <div className="flex flex-col justify-between h-[calc(100dvh-32px)] py-12 w-full px-4">
          <div className="flex flex-col items-center px-4 gap-y-1 md:mt-[12dvh]">
            <p className="text-xl font-medium">
              {participant?.firstName} {participant?.lastName}
            </p>
            <p className="text-xl text-gray-300">
              <span className="tabular-nums">{callDuration}</span>
            </p>
            <Avatar className="h-40 w-40 mt-12">
              {participant?.avatar ? (
                <AvatarImage src={participant.avatar} />
              ) : (
                <AvatarFallback className="text-5xl text-primary">{participant?.firstName?.charAt(0) ?? '?'}</AvatarFallback>
              )}
            </Avatar>
          </div>

          <div className="flex items-center justify-center gap-x-10 mb-8 md:mb-32">
            <DeviceSelector
              selectedAudioInput={selectedAudioInput}
              selectedAudioOutput={selectedAudioOutput}
              audioInputs={audioInputs}
              audioOutputs={audioOutputs}
              onAudioInputChange={(id) => void selectAudioInput({ id })}
              onAudioOutputChange={(id) => void selectAudioOutput({ id })}
              buttonSize="lg"
            />

            <PeripheralToggleButton
              icon="microphone"
              isActive={!muted}
              onClick={toggleMute}
              aria-label={muted ? 'Activer le micro' : 'Couper le micro'}
              buttonSize="lg"
            />

            <MeetingActionButton
              icon={XIcon}
              onClick={leave}
              variant="destructive"
              disabled={isLeaving}
              buttonSize="lg"
            />
          </div>
        </div>
      </AppTemplate>
    </>
  )
}
